import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Formik, Form } from 'formik';
import useAccount from '../hooks/useAccount';
import {
  Optioner,
  ColorPalettes,
  Grids,
  Breakpoints,
} from 'kinedu-react-components';
import { Button, Texter } from 'lego-dist';
import * as Yup from 'yup';
import { ROLES } from '../constants/onBoarding';
import { IformData } from '../containers/ObContainer';
import EventReporter from 'appEvents';
import { AppEvents } from '../constants/events';
import { KEYS_LOCALSTORAGE } from 'constants/general';

const { KineduPalette } = ColorPalettes;

const StyledProfileView = styled.div`
${Grids.mobile}
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;

    width: 100%;
    position: absolute;
    padding:10px 35px;
    overflow: auto;

    .texter {
      display:flex;
      flex-direction: column;
      padding: 0px 20px;
      margin-bottom: 20px;

      .texter-label {
        letter-spacing: 0;
        text-align:center;
        font-size: 18px;
        color: ${KineduPalette.shadeDark};
        text-transform: unset;
        font-weight: bold;
      }
    }

    .Optioner {
      display: flex;
      align-items: center;

      margin: auto 0 20px;

      .Optioner-box {
        width: 140px;
      }

      .Optioner-checkbox-group {
        justify-content: center;
      }

      .Optioner-label {
        text-align: left;
        text-transform: unset;
        color: ${KineduPalette.shadeDark};
        font-weight: bold;
        letter-spacing: 0;
        font-size: 18px;
        margin-bottom: 20px;
      }
    }
    ${Breakpoints.desktop} {
      ${Grids.ob}
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      box-sizing: border-box;

      width: 100%;
      height: 100%;
      position: absolute;
      padding: 20px 55px;
      overflow: auto;

      .texter {
        display:flex;
        flex-direction: column;

        max-width: 701px;
        margin-bottom: 20px;

        .texter-label {
          letter-spacing: 0;
          text-align:left;
          font-size: 18px;
          color: ${KineduPalette.shadeDark};
          text-transform: unset;
          font-weight: bold;
        }
      }

      .Optioner {
        display: flex;
        align-items: flex-start;

        margin: auto 0 30px;

        .Optioner-box {
          width: 160px;
        }

        .Optioner-checkbox-group {
          justify-content: space-between;
        }

        .Optioner-label {
          text-align: left;
          text-transform: unset;
          color: ${KineduPalette.shadeDark};
          font-weight: bold;
          letter-spacing: 0;
          font-size: 18px;
          margin-bottom: 20px;
        }
      }
    }
`;

const Wrapper = styled.div`
${Grids.mobile}
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;

  width: 100%;
  margin: auto;

  .profile-button {
    display: flex;
    align-self: center;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    margin: auto;
  }
  ${Breakpoints.desktop} {
    ${Grids.ob}
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-content: center;

    width: 100%;
    margin: auto;

    .profile-button {
      display: flex;
      align-self: center;
      justify-content: center;
      align-items: center;
      flex-direction: column;

      margin: auto;
    }
  }
`;

const Title = styled.span`
  ${Grids.mobile}
  display: block;
  margin: 20px 0;
  align-self: center;

  text-transform: capitalize;
  text-align: center;
  font-size: 28px;
  font-weight: bold;
  line-height: 36px;
  color: ${KineduPalette.newKinedu};
  ${Breakpoints.desktop} {
    ${Grids.ob}
    display: block;
    margin: auto 0 0;
    align-self: flex-start;

    text-transform: capitalize;
    text-align: left;
    font-size: 36px;
    font-weight: bold;
    line-height: 47px;
  }
`;

interface ProfileViewProps {
  nextStep: (values: {}) => void;
  formData: IformData;
}

const ProfileView: React.SFC<ProfileViewProps> = ({ nextStep, formData }) => {
  const { t } = useTranslation();
  const { user } = useAccount();
  const schema = Yup.object().shape({
    title: Yup.string().trim().required(),
    name: Yup.string().trim().required(),
    lastname: Yup.string().trim().required(),
    schoolname: Yup.string().trim().required(),
  });

  const initialValues = {
    title: formData.title ? formData.title : '',
    name: formData.name ? formData.name : '',
    lastname: formData.lastname ? formData.lastname : '',
    schoolname: formData.schoolname ? formData.schoolname : '',
  };

  // VALIDATE
  useMemo(() => {
    if (user.id) {
      EventReporter.view(AppEvents.S_OB_PROFILE());
      localStorage.setItem(KEYS_LOCALSTORAGE.VIEW_HOW_IT_WORKS, 'true');
    }
  }, [user.id]);

  const translatedRoles = ROLES.map((item) => ({ ...item, label: t(`ob:ROLES.${item.label}`) }));

  return (
    <StyledProfileView>
      <Title>{t('ob:SET_YOUR_PROFILE')}</Title>
      <Wrapper>
        <Formik
          key={initialValues.name}
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={(values, actions) => {
            actions.setSubmitting(false);
            nextStep(values);
          }}
          isInitialValid={schema.isValidSync(initialValues)}
          enableReinitialize
          render={({ setFieldValue, isValid, values }) => (
            <Form>
              <Optioner
                label={t('ob:I_AM_A')}
                theme="OutlineFlat"
                options={translatedRoles}
                value={values.title}
                onChange={(e: any) => setFieldValue('title', e)}
                radio
              />
              <Texter
                label={t('ob:MY_FIRST_NAME_IS')}
                value={values.name}
                onChange={(e: any) => setFieldValue('name', e)}
              />
              <Texter
                label={t('ob:MY_LAST_NAME_IS')}
                value={values.lastname}
                onChange={(e: any) => setFieldValue('lastname', e)}
              />
              <Texter
                label={t('ob:MY_SCHOOL_IS')}
                value={values.schoolname}
                onChange={(e: any) => setFieldValue('schoolname', e)}
              />
              <Button
                type="submit"
                color="primary"
                className="profile-button"
                style={{ fontFamily: 'Proxima Nova' }}
                maxWidth={300}
                fill
                flat
                rounded
                disabled={!isValid}
              >
                {t('ob:CONTINUE')}
              </Button>
            </Form>
          )}
        />
      </Wrapper>
    </StyledProfileView>
  );
};

export default ProfileView;
